import { apiClient } from './client';

export interface ClientAdminContact {
  name: string | null;
  email: string | null;
}

export interface ClientManager {
  id: number;
  name: string;
}

/** Один клиент – как отдаёт BFF (проксирует /admin-api/v1/customers старой админки). */
export interface ClientListItem {
  id: number;
  /** Laravel: Account.name ("Наименование компании" в Orchid) – может быть пустым. */
  company: string | null;
  /** Статус кабинета (active/demo/disable и т.п.), НЕ статус лицензии. */
  status: string;
  /** Дата отключения кабинета, НЕ срок действия лицензии. */
  validity: string | null;
  /** Тип тарификации (license/ue). */
  tariffType: string;
  balance: number;
  /** Доступно (как в старой админке): баланс за вычетом резерва/незавершённых сеансов. */
  availableBalance: number;
  /** Дата подключения кабинета. */
  connectedAt: string | null;
  adminContact: ClientAdminContact;
  testsCount: number;
  managers: ClientManager[];
  /** Последнее событие кабинета – как в «Журнале событий ЛК» старой админки. */
  lastActivityAt: string | null;
  /** "Замочек" Вход в Studio/Вход в Maintest 5i – один флаг на обе ссылки. */
  canImpersonate: boolean;
}

export interface ClientsPageMeta {
  currentPage: number;
  lastPage: number;
  perPage: number;
  total: number;
}

export interface ClientsPage {
  items: ClientListItem[];
  meta: ClientsPageMeta;
}

export interface FetchClientsParams {
  page?: number;
  /** Прокидывается в Laravel как filter[key]=value (Filterable-трейт на Account). */
  filter?: Record<string, string>;
  /** `column` – по возрастанию, `-column` – по убыванию (Filterable-трейт, allowedSorts на Account). */
  sort?: string;
}

/** Список клиентов текущего менеджера. 401 → не авторизован. */
export async function fetchClients(params: FetchClientsParams = {}): Promise<ClientsPage> {
  const query: Record<string, unknown> = {};
  if (params.page) query.page = params.page;
  if (params.sort) query.sort = params.sort;
  if (params.filter) {
    for (const [key, value] of Object.entries(params.filter)) {
      query[`filter[${key}]`] = value;
    }
  }
  const { data } = await apiClient.get<ClientsPage>('/clients', { params: query });
  return data;
}

/** Запись истории изменений – AdminHistory ("События админки"), без view-действий. */
export interface ClientHistoryEntry {
  action: string;
  author: string | null;
  status: string;
  date: string | null;
}

/** Карточка клиента – как отдаёт BFF (проксирует /admin-api/v1/customers/{id}). */
export interface ClientDetail {
  id: number;
  company: string | null;
  /** Account.name – внутреннее имя кабинета в Maintest 5i, НЕ company_name. */
  mt1Name: string;
  status: string;
  /** external|internal. */
  type: string | null;
  validity: string | null;
  tariffType: string;
  /** Разрешён ли вход в Maintest-5i через персональный холл (Account.is_mt1_hall_allowed). */
  isMt1HallAllowed: boolean;
  /** Account.description – свободный текст про клиента (сейчас нигде не показывался в Orchid). */
  notes: string | null;
  managers: ClientManager[];
  adminContact: {
    name: string | null;
    /** Разбивка по частям — для формы редактирования (email не входит, он не редактируется). */
    lastName: string | null;
    firstName: string | null;
    middleName: string | null;
    position: string | null;
    email: string | null;
    phone: string | null;
  };
  personalData: {
    /** Для дефолтного значения select'а в форме редактирования; null = "По умолчанию". */
    operatorTemplateId: number | null;
    operatorTemplate: string;
    ogrn: string | null;
    address: string | null;
  };
  balance: {
    tariffType: string;
    value: number;
    availableBalance: number;
  };
  /** "Замочек" Вход в Studio/Вход в Maintest 5i (карточка клиента). */
  canImpersonate: boolean;
  /** Есть ли у клиента свои пользователи – от этого зависит видимость вкладки "Пользователи". */
  hasUsers: boolean;
  canRemove: boolean;
  canSendCredentials: boolean;
  canEditTutors: boolean;
}

/** Карточка клиента. 401 → не авторизован, 403/404 пробрасываются как есть. */
export async function fetchClient(id: number | string): Promise<ClientDetail> {
  const { data } = await apiClient.get<ClientDetail>(`/clients/${id}`);
  return data;
}

/**
 * История изменений – отдельный запрос (не часть fetchClient), чтобы вкладка
 * сама обновлялась при каждом открытии, а не застревала на снимке с момента
 * первой загрузки карточки.
 */
export async function fetchClientHistory(id: number | string): Promise<ClientHistoryEntry[]> {
  const { data } = await apiClient.get<ClientHistoryEntry[]>(`/clients/${id}/history`);
  return data;
}

/** Строка полного каталога библиотечных тестов (не только подключённых) – только вкл/выкл + базовая цена как справка. */
export interface ClientLibraryTestRow {
  id: number;
  name: string;
  category: string | null;
  active: boolean;
  basePrice: number;
  version: string | null;
}

export interface FetchClientTestsParams {
  search?: string;
  group?: string;
  page?: number;
  /** Только подключённые — блок "Подключено" в LibraryTestsPanel, без учёта текущей страницы/поиска каталога. */
  attached?: boolean;
  perPage?: number;
}

/** Постранично — библиотека тестов растёт вместе с main_tests, см. докблок CustomerController::tests() в Laravel. */
export interface ClientTestsPage {
  items: ClientLibraryTestRow[];
  meta: ClientsPageMeta;
}

/** Каталог библиотечных тестов клиента, подключённые – первыми (в рамках текущей страницы). */
export async function fetchClientTests(
  id: number | string,
  params: FetchClientTestsParams = {},
): Promise<ClientTestsPage> {
  const { data } = await apiClient.get<ClientTestsPage>(`/clients/${id}/tests`, {
    params: { ...params, attached: params.attached ? 1 : undefined },
  });
  return data;
}

export interface UpdateClientTestPayload {
  attached?: boolean;
  cost?: number;
}

/**
 * Вкл/выкл теста клиенту или смена его стоимости – реальная активация в MT1
 * (см. докблок CustomerController::updateTest() в Laravel), поэтому таймаут
 * больше стандартного – BFF там сам ждёт до 30с апстрим.
 */
export async function updateClientTest(
  id: number | string,
  testId: number,
  payload: UpdateClientTestPayload,
): Promise<{ success: boolean }> {
  const { data } = await apiClient.put<{ success: boolean }>(
    `/clients/${id}/tests/${testId}`,
    payload,
    {
      timeout: 35_000,
    },
  );
  return data;
}

/**
 * Строка таблицы цен (вкладка "Цены"): library – только подключённые
 * библиотечные тесты, monoscale – все моношкальные (доступны всегда).
 * modifiedBy – best-effort (см. докблок CustomerController::prices() в
 * Laravel), может быть null даже при заполненном modifiedAt.
 */
export interface ClientPriceRow {
  id: number;
  name: string;
  category: string | null;
  basePrice: number;
  clientPrice: number;
  modifiedAt: string | null;
  modifiedBy: string | null;
  version: string | null;
}

export interface FetchClientPricesParams {
  scope: 'library' | 'monoscale';
  search?: string;
  group?: string;
  page?: number;
}

/** Постранично — см. докблок CustomerController::prices() в Laravel. */
export interface ClientPricesPage {
  items: ClientPriceRow[];
  meta: ClientsPageMeta;
}

/** Таблица цен клиента. */
export async function fetchClientPrices(
  id: number | string,
  params: FetchClientPricesParams,
): Promise<ClientPricesPage> {
  const { data } = await apiClient.get<ClientPricesPage>(`/clients/${id}/prices`, { params });
  return data;
}

export interface UpdateClientBalancePayload {
  action: 'add' | 'subtract';
  amount: number;
  comment?: string;
}

export interface UpdateClientBalanceResult {
  success: boolean;
  value: number;
  availableBalance: number;
}

/** Выдать (add) или списать (subtract) у.е. с комментарием – реальная операция, не стаб. */
export async function updateClientBalance(
  id: number | string,
  payload: UpdateClientBalancePayload,
): Promise<UpdateClientBalanceResult> {
  const { data } = await apiClient.post<UpdateClientBalanceResult>(
    `/clients/${id}/balance`,
    payload,
  );
  return data;
}

/**
 * Список групп для фильтра (вкладки "Тесты"/"Цены") — не зависит от текущей
 * (возможно узкой/пустой) выборки строк, иначе фильтр то есть, то нет в
 * зависимости от того, у скольких из уже загруженных тестов заполнена группа.
 */
export interface ClientTestGroups {
  groups: string[];
  /** Значение для фильтра "Группа не задана" — передавать этой же строкой в group=. null, если таких тестов нет. */
  ungroupedValue: string | null;
}

export async function fetchClientTestGroups(
  id: number | string,
  scope: 'library' | 'monoscale',
): Promise<ClientTestGroups> {
  const { data } = await apiClient.get<ClientTestGroups>(`/clients/${id}/test-groups`, {
    params: { scope },
  });
  return data;
}

/** Готовый URL для window.open() – не требует сессии в старой /admin (см. докблок ImpersonateUrlService в Laravel). */
export interface ClientImpersonateResult {
  url: string;
}

/**
 * "Вход в Studio"/"Вход в Maintest 5i" — список (иконка в строке) и карточка
 * клиента используют её без userId (админ аккаунта), вкладка "Пользователи" —
 * с userId конкретного пользователя.
 */
export async function fetchClientImpersonateUrl(
  id: number | string,
  service: 'studio' | 'maintest5i',
  userId?: number,
): Promise<ClientImpersonateResult> {
  const { data } = await apiClient.get<ClientImpersonateResult>(`/clients/${id}/impersonate`, {
    params: { service, userId },
  });
  return data;
}

/** "Управление лицензиями" — тот же паттерн, что fetchClientImpersonateUrl(). */
export async function fetchClientMt1LicensesUrl(id: number | string): Promise<ClientImpersonateResult> {
  const { data } = await apiClient.get<ClientImpersonateResult>(`/clients/${id}/mt1-licenses-url`);
  return data;
}

/** Строка вкладки "Пользователи" — собственные пользователи клиента (не Orchid-кураторы). */
export interface ClientAccountUser {
  id: number;
  firstName: string | null;
  lastName: string | null;
  role: string;
  status: string;
  logins: string[];
  canImpersonateStudio: boolean;
  canImpersonateMt1: boolean;
}

export async function fetchClientUsers(id: number | string): Promise<ClientAccountUser[]> {
  const { data } = await apiClient.get<ClientAccountUser[]>(`/clients/${id}/users`);
  return data;
}

export interface ClientEditOption {
  id: number;
  name: string;
}

/** Справочники для формы редактирования (шаблоны соглашений, кураторы). */
export interface ClientEditOptions {
  operatorTemplates: ClientEditOption[];
  tutors: ClientEditOption[];
  /** Id текущего пользователя среди tutors — у сессии (AuthUser) нет числового id, только email/name. */
  currentUserId: number;
}

export async function fetchClientEditOptions(): Promise<ClientEditOptions> {
  const { data } = await apiClient.get<ClientEditOptions>('/clients/edit-options');
  return data;
}

/** Тело PUT /clients/{id} – полное редактирование карточки (вкладка "Общее"). */
export interface UpdateClientPayload {
  name: string;
  status: string;
  type: string | null;
  tariffType: string;
  validity: string | null;
  isMt1HallAllowed: boolean;
  notes: string | null;
  personalData: {
    operatorTemplateId: number | null;
    ogrn: string | null;
    address: string | null;
  };
  adminContact: {
    lastName: string | null;
    firstName: string | null;
    middleName: string | null;
    phone: string | null;
    position: string | null;
  };
  tutorIds: number[];
}

/**
 * Сохранение карточки клиента — как и updateClientTest(), может реально
 * сходить в MT1/Bitrix (см. докблок CustomerController::update() в Laravel),
 * поэтому такой же увеличенный таймаут.
 */
export async function updateClient(
  id: number | string,
  payload: UpdateClientPayload,
): Promise<ClientDetail> {
  const { data } = await apiClient.put<ClientDetail>(`/clients/${id}`, payload, {
    timeout: 35_000,
  });
  return data;
}

/**
 * Тело POST /clients – создание клиента (мастер ClientCreatePage). Отличия
 * от UpdateClientPayload: mt1Login и adminContact.email задаются только
 * здесь (после создания не редактируются, см. UpdateClientPayload/
 * ClientDetail), plus необязательный balanceAction (выдать/списать сразу
 * при создании, только для тарифа "у.е.").
 */
export interface CreateClientPayload {
  name: string;
  mt1Login: string;
  type: string | null;
  tariffType: string;
  status: string;
  validity: string | null;
  isMt1HallAllowed: boolean;
  notes: string | null;
  personalData: {
    operatorTemplateId: number | null;
    ogrn: string | null;
    address: string | null;
  };
  adminContact: {
    lastName: string | null;
    firstName: string | null;
    middleName: string | null;
    phone: string | null;
    position: string | null;
    email: string;
  };
  tutorIds: number[];
  balanceAction: { action: 'add' | 'subtract'; amount: number; comment?: string } | null;
}

/**
 * Создание клиента — реально ходит в SSO синхронно (регистрация логина
 * админа), таймаут больше обычного увеличенного (см. updateClient()) — на
 * Windows сам этот SSO-вызов на стороне Laravel может зависать на TCP-коннекте
 * заметно дольше своего номинального таймаута (см. докблок createCustomer()
 * в BFF clients.service.ts), но аккаунт всё равно создаётся до конца —
 * обрывать ожидание раньше значит показывать ошибку по уже удавшемуся созданию.
 */
export async function createClient(payload: CreateClientPayload): Promise<ClientDetail> {
  const { data } = await apiClient.post<ClientDetail>('/clients', payload, {
    timeout: 95_000,
  });
  return data;
}

/** Удаление клиента — хард-делет, без возможности восстановить. */
export async function deleteClient(id: number | string): Promise<{ success: boolean }> {
  const { data } = await apiClient.delete<{ success: boolean }>(`/clients/${id}`);
  return data;
}

/**
 * Отправить учётные данные админу клиента — рейт-лимит 15 минут приходит от
 * апстрима как 429. Реально ходит во внешний auth-сервис и дальше в отправку
 * письма (см. докблок ClientsService.sendCredentials() в BFF), поэтому
 * увеличенный таймаут — как у updateClient()/updateClientTest().
 */
export async function sendClientCredentials(id: number | string): Promise<{ success: boolean }> {
  const { data } = await apiClient.post<{ success: boolean }>(
    `/clients/${id}/send-credentials`,
    undefined,
    { timeout: 50_000 },
  );
  return data;
}
